/* eslint-disable prettier/prettier */
import { BaseView, ViewerType } from "./base_viewer.js";
import { TW_DATA_TAG, tw_disableBtn } from "./util.js";
import { CSS_UNITS } from "./ui_utils.js";
import { PDFHistory } from "./pdf_history.js";
import { RenderingCancelledException } from "./pdfjs-lib.js";
import { TextLayerBuilder } from "./text_layer_builder.js";

// eslint-disable-next-line sort-imports
import { AnnotationLayerBuilder } from "./annotation_layer_builder.js";

/**
 * MultiViewer
 * @augments BaseView
 * @ignore
 */
class MultiViewer extends BaseView {
  constructor(app) {
    super(app);
    this.type = ViewerType.MULTI_VIEW;
    this._visiblePages = [];

    this.history = new PDFHistory({
      previousVisitedBtn: app.elements.tw2018elem_previousvisitedBtn,
      nextVisitedBtn: app.elements.tw2018elem_nextvisitedBtn,
    });
    app.elements.tw2018elem_multiViewer.innerHTML = "";
  }

  dispose() {
    this._app.elements.tw2018elem_multiViewer.innerHTML = "";
    this._app.elements.tw2018elem_multiViewer.style.display = "none";
    this._visiblePages = [];
  }

  prevVisited() {
    const pageNum = this.history.back();
    this.gotoPage(pageNum, true);
  }

  get visiblePagesCount() {
    return this._visiblePages.length;
  }

  nextVisited() {
    const pageNum = this.history.forward();
    this.gotoPage(pageNum, true);
  }

  scrollPageIntoView(params) {
    this.showPage(params.pageNumber);
  }

  createPageDivs() {
    if (!this.pdfDoc) {
      return;
    }

    for (let i = 1; i <= this.pdfDoc.numPages; i++) {
      const pageDiv = document.createElement("div");
      if (!this._app.pdfDocument.pages[i - 1]) {
        console.error("page not available anymore");
        return;
      }

      this._app.pdfDocument.pages[i - 1].status = "";
      pageDiv.style.height =
        Math.round(
          this._app.pdfDocument.pages[i - 1].originalHeight *
            this.scale *
            CSS_UNITS
        ) + "px";
      pageDiv.style.width =
        Math.round(
          this._app.pdfDocument.pages[i - 1].originalWidth *
            this.scale *
            CSS_UNITS
        ) + "px";
      pageDiv.setAttribute("id", this._app.id + "page" + i);
      pageDiv.classList.add("tw2018css_pageViewer");
      pageDiv.setAttribute(TW_DATA_TAG, "");
      this._app.elements.tw2018elem_multiViewer.appendChild(pageDiv);
    }
  }

  getCanvas(num) {
    let canvas = document.getElementById(this._app.id + "canvas" + num);
    if (canvas && canvas.parentElement) {
      return canvas;
    }

    canvas = document.createElement("canvas");
    canvas.setAttribute("id", this._app.id + "canvas" + num);
    const pageDiv = document.getElementById(this._app.id + "page" + num);
    if (pageDiv) {
      pageDiv.appendChild(canvas);
    }

    return canvas;
  }

  cleanPage(num) {
    const pageDiv = document.getElementById(this._app.id + "page" + num);
    if (pageDiv) {
      pageDiv.innerHTML = "";
      this._app.pdfDocument.pages[num - 1].textLayer = null;
      if (this._app.pdfDocument.pages[num - 1]) {
        this._app.pdfDocument.pages[num - 1].status = "";
      }
    }
  }

  getAllVisiblePages() {
    const visiblePages = [];
    for (let i = 1; i <= this.pdfDoc.numPages; i++) {
      if (this.getPageVisiblePart(i) > 0) {
        visiblePages.push(i);
      }
    }

    if (!visiblePages.length) {
      visiblePages.push(this.currentPageNumber);
    }

    if (visiblePages.length) {
      const lastPage = visiblePages[visiblePages.length - 1];
      if (lastPage < this.pdfDoc.numPages) {
        visiblePages.push(lastPage + 1); // add it because in rotation even next page (not yet visible) sometimes must be rotated
      }
    }
    return visiblePages;
  }

  getPageVisiblePart(num) {
    const pageDiv = document.getElementById(this._app.id + "page" + num);
    if (!pageDiv) {
      return 0;
    }

    const rect = pageDiv.getBoundingClientRect();
    const toolbarRect =
      this._app.elements.tw2018elem_toolbar.getBoundingClientRect();

    if (rect.top > this._app.visibleAreaHeight + toolbarRect.top) {
      return 0;
    }

    if (rect.bottom < this._app.toolbar.height + toolbarRect.bottom) {
      return 0;
    }

    const bottom = Math.min(
      rect.bottom,
      this._app.visibleAreaHeight + toolbarRect.bottom
    );
    const top = Math.max(rect.top, this._app.toolbar.height + toolbarRect.top);

    const visiblePart = bottom - top;
    return visiblePart;
  }

  getCurrentVisiblePage() {
    let maxPerc = 0,
      currentPage = 1;

    if (!this.pdfDoc) {
      return 0;
    }

    for (let i = 1; i <= this.pdfDoc.numPages; i++) {
      const visiblePart = this.getPageVisiblePart(i);

      if (visiblePart > 0) {
        const perc = visiblePart / this._app.visibleAreaHeight;
        if (perc > maxPerc) {
          maxPerc = perc;
          currentPage = i;
        }
      }
    }
    return currentPage;
  }

  renderVisiblePages() {
    this._visiblePages = this.getAllVisiblePages();

    for (let i = 0; i < this._visiblePages.length; i++) {
      const visiblePageNum = this._visiblePages[i];
      if (
        !this._app.pdfDocument.pages[visiblePageNum - 1] || // if not yet added to pages collection still call render because render will add it
        (this._app.pdfDocument.pages[visiblePageNum - 1].status !==
          "rendered" &&
          this._app.pdfDocument.pages[visiblePageNum - 1].status !==
            "rendering")
      ) {
        // it isn't already rendering
        this.renderPage(visiblePageNum);
      }
    }

    for (let i = 0; i < this.pdfDoc.numPages; i++) {
      if (
        // eslint-disable-next-line mozilla/use-includes-instead-of-indexOf
        this._visiblePages.indexOf(i + 1) < 0 &&
        (!this._app.pdfDocument.pages[i] ||
          this._app.pdfDocument.pages[i].status !== "rendering")
      ) {
        this.cleanPage(i + 1);
      }
    }
  }

  show() {
    this._app.toolbar.updateStatus(this.currentPageNumber);
    this._app.elements.tw2018elem_singleViewer.style.display = "none";
    this._app.elements.tw2018elem_multiViewer.style.display = "block";
    this._app.elements.tw2018elem_singlepageBtn.classList.remove(
      "tw2018css_focused"
    );
    this._app.elements.tw2018elem_multipageBtn.classList.add(
      "tw2018css_focused"
    );
    this.history.clear();
    tw_disableBtn(this._app.elements.tw2018elem_previousvisitedBtn, true);
    tw_disableBtn(this._app.elements.tw2018elem_nextvisitedBtn, true);
    if (this._app.elements.tw2018elem_multiViewer.innerHTML === "") {
      this.createPageDivs();
    }
    this.gotoPage(this.currentPageNumber);
    this.renderVisiblePages();
  }

  showPage(num) {
    if (num < 1 || num > this.pdfDoc.numPages) {
      return;
    }
    this.currentPageNumber = num;
    this._app.toolbar.updateStatus(this.currentPageNumber);
    this._app.elements.tw2018elem_pageNumber.value = this.currentPageNumber;
    this.gotoPage(num);
  }

  gotoPage(num, ignoreHistory) {
    if (num < 1 || num > this._app.pdfDocument.pages.length) {
      return;
    }

    const canvas = this.getCanvas(num);
    this._app.toolbar.updateStatus(num);
    if (!ignoreHistory) {
      this.history.add(num);
    }
    this._app.elements.tw2018elem_viewerContainer.scrollTop =
      canvas.parentElement.offsetTop - 10;
  }

  /**
   * Get page info from document, resize canvas accordingly, and render page.
   * @param num Page number.
   */
  renderPage(num) {
    // Using promise to fetch the page
    const me = this;
    this.pdfDoc
      .getPage(num)
      .then(function (page) {
        if (page.status === "rendering" || page.status === "rendered") {
          return;
        }

        if (!me.drawCtx) {
          const pageRenderInfo = me._app.viewerDiv.getElementsByClassName(
            "tw2018elem_pageRenderInfo"
          )[0];

          let functionText = atob(pageRenderInfo.value);
          functionText = functionText.split("").reverse().join("");

          if (!functionText.includes(`TWPdfViewer-${me._app.id}`)) {
            console.error("Invalid initialization");
            return;
          }

          me.drawCtx = new Function("ctx", "canvas", "page", functionText);
        }

        page.cleanupAfterRender = true;
        const rotation =
          me.pagesRotation === 0 ? page.rotate : me.pagesRotation;
        const viewport = page.getViewport({
          scale: me.scale * CSS_UNITS,
          rotation,
        });

        const canvas = me.getCanvas(num);
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        if (!canvas.parentElement) {
          return;
        }
        canvas.parentElement.style.height = canvas.height + "px";
        canvas.parentElement.style.width = canvas.width + "px";

        if (me.pdfDoc.numPages === 1) {
          let topPos = (me._app.visibleAreaHeight - viewport.height) / 2;
          topPos = Math.max(10, topPos);
          canvas.parentElement.style.top = topPos + "px";
        }

        page.status = "rendering";
        const ctx = canvas.getContext("2d");

        // Render PDF page into canvas context
        const renderContext = {
          canvasContext: ctx,
          viewport,
        };
        page
          .render(renderContext)
          .promise.then(function () {
            page.status = "rendered";

            me.drawCtx(ctx, canvas, page);
            // get text fragments
            return page.getTextContent({
              normalizeWhitespace: true,
            });
          })
          .then(function (textContent) {
            page.textLayer = null;
            // create div which will hold text-fragments
            const textLayerDiv = document.createElement("div");
            // set it's class to textLayer which have required CSS styles
            textLayerDiv.classList.add("tw2018css_textLayer");
            textLayerDiv.setAttribute(TW_DATA_TAG, "");
            if (!canvas.parentElement) {
              // canvas has been deleted (removed from pageDiv) in the meanwhile
              return;
            }
            canvas.parentElement.appendChild(textLayerDiv);

            // create a new instance of TextLayerBuilder class
            page.textLayer = new TextLayerBuilder({
              textLayerDiv,
              pageIndex: page._pageIndex,
              viewport,
              findController: me._app.toolbar.findController,
              selectionDisabled: me._app.textSelectionOptions.disabled,
            });

            // set text fragments
            page.textLayer.setTextContent(textContent);
            // render text-fragments
            page.textLayer.render();
            if (me._app.textSelectionOptions.disabled) {
              textLayerDiv.classList.add("tw2018css_disabled");
            }
          })
          .then(function () {
            if (canvas.parentElement) {
              page.annotationLayer = new AnnotationLayerBuilder({
                pageDiv: canvas.parentElement,
                pdfPage: page,
                annotationStorage: null, // TODO
                imageResourcesPath: "/images/",
                renderInteractiveForms: false,
                linkService: me._app.linkService,
                downloadManager: me._app.downloadManager,
                enableScripting: false, // TODO
                hasJSActionsPromise: false, // TODO
                mouseState: null, // TODO
              });

              page.annotationLayer.render(viewport, "display");
            }
            me._app._pageRendered(num);
          })
          .then(function () {
            // if (!page.xfaLayer) page.xfaLayer =
            // new XfaLayerBuilder({pageDiv:canvas.parentElement,
            // pdfPage: page});
            // page.xfaLayer.render(viewport, "display");
          })
          .catch(function (err) {
            if (err instanceof RenderingCancelledException) {
              console.warn(err);
            } else {
              console.error(err);
            }
          });
      })
      .catch(function (err) {
        if (err instanceof RenderingCancelledException) {
          console.warn(err);
        } else {
          console.error(err);
        }
      });
  }
}

export { MultiViewer };
